import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, PlugZap, Server } from 'lucide-react';
import syncService from '../../services/syncService';
import toolkitService from '../../services/ToolkitService';
import Button from '../shared/Button';
import { STEP, stepIcon } from './ToolkitFileStep';
import ToolkitManualRun from './ToolkitManualRun';

// The Toolkit lives inside a sync project (/sync/:syncId/toolkit/...). Every Toolkit screen uses the project's own
// OPERA Cloud connection, so there is nothing to pick: these two give a screen its project and show which
// connection it is working through.
//
// useToolkitProject() → { syncId, project (the sync project's details), ctx ({ syncId } for toolkitService calls),
//                         error, loading }
// <ToolkitProjectHeader title subtitle /> — back links, the project and its target, and a connection test. It also
// says when the project has NO SyncConnect, and mounts the "run it yourself" dialog (ToolkitManualRun) that the
// v5-database screens fall back to then.

export const useToolkitProject = () => {
    const { syncId } = useParams();
    const [project, setProject] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        let live = true;
        setProject(null);
        setError(null);
        syncService.getSyncProjectDetails(syncId)
            .then((d) => {
                if (!live) return;
                const p = d.projectDetails || {};
                if (!p.syncProjectId) setError('Sync project not found');
                else if (p.usesMigrationToolkit !== 'Y') setError('This project\'s template does not use the Migration Toolkit');
                setProject(p);
            })
            .catch(() => live && setError('Failed to load the sync project'));
        return () => { live = false; };
    }, [syncId]);
    const ctx = useMemo(() => ({ syncId }), [syncId]);
    return { syncId, project, ctx, error, loading: !project && !error, ready: !!project && !error };
};

export function ToolkitProjectHeader({ toolkit, title, subtitle, isOverview = false }) {
    const navigate = useNavigate();
    const { syncId, project, ctx, error } = toolkit;
    const [test, setTest] = useState({ state: STEP.IDLE, message: '' });
    // A server job running for this project shares the one toolkit lambda with this screen: say so, so a slower
    // answer here is not a mystery (jobs.js — the screen's calls are retried while the lambda is busy).
    const [activeJob, setActiveJob] = useState(null);
    useEffect(() => {
        if (!project || error) return;
        toolkitService.jobs(ctx, 'list', { active: true, limit: 1 })
            .then(({ data }) => setActiveJob(data.items[0] || null)).catch(() => {});
    }, [project, error, ctx]);

    const testConnection = async () => {
        setTest({ state: STEP.BUSY, message: 'Connecting…' });
        try {
            const { data } = await toolkitService.testConnection(ctx);
            setTest({ state: STEP.DONE, message: `Connected to ${data.hotelId} in ${data.tookMs} ms` });
        } catch (err) {
            setTest({ state: STEP.ERROR, message: err?.response?.data?.message || err?.response?.data?.error?.message || err.message });
        }
    };

    return (
        <>
            <div className="tkb-crumbs">
                <button type="button" className="tkb-link" onClick={() => navigate(`/sync/${syncId}`)}>
                    <ArrowLeft size={14} /> {project?.projectName || 'Sync project'}
                </button>
                {!isOverview && (
                    <>
                        <span>/</span>
                        <button type="button" className="tkb-link" onClick={() => navigate(`/sync/${syncId}/toolkit`)}>Toolkit</button>
                    </>
                )}
            </div>
            <div className="otx-header">
                <div>
                    <h1>{title}</h1>
                    {subtitle && <p className="otx-subtitle">{subtitle}</p>}
                </div>
            </div>
            {error && <div className="tkb-warn">{error}</div>}
            {project && !error && (
                <div className="tkb-step tkb-project">
                    <div>
                        <div className="tkb-project-label">Working through this project's OPERA Cloud connection</div>
                        <div className="tkb-project-name">
                            {project.targetConnectorName || project.targetSystemName || 'Target connector'}
                            {project.hotelName ? ` · ${project.hotelName}` : ''}
                        </div>
                    </div>
                    <div className="tkb-project-test">
                        <Button variant="secondary" onClick={testConnection} disabled={test.state === STEP.BUSY}>
                            <PlugZap className="icon" /> Test connection
                        </Button>
                        {stepIcon(test.state)}
                        {test.message && <span className={`tkb-msg${test.state === STEP.ERROR ? ' tkb-fail' : ''}`}>{test.message}</span>}
                    </div>
                </div>
            )}
            {activeJob && (
                <div className="tkb-job-banner">
                    <Server size={15} />
                    <span><strong>A server job is running for this project:</strong> {activeJob.title} — {activeJob.pending} of {activeJob.total} row(s) to go.
                        Requests from this screen share the Toolkit with it, so they may take a little longer until it finishes.</span>
                    {activeJob.screenPath && (
                        <button type="button" className="tkb-link" onClick={() => navigate(`${activeJob.screenPath}?job=${activeJob.id}`)}>Open it</button>
                    )}
                </div>
            )}
            {project && !error && project.usesSyncConnect !== 'Y' && (
                <div className="tkb-warn">
                    <strong>This project has no SyncConnect.</strong> The Toolkit cannot reach the property's OPERA v5 database itself,
                    so screens that read or change it will give you a SQL script to run in SQL Developer and ask you to upload its
                    result (CSV or Excel). To have the Toolkit do this for you, install SyncConnect at the property and{' '}
                    <button type="button" className="tkb-link" onClick={() => navigate(`/sync/${syncId}/edit`)}>turn it on for the project</button>.
                    Screens that work from Opera Cloud or from v5 report files are not affected.
                </div>
            )}
            {project && !error && <ToolkitManualRun ctx={ctx} />}
        </>
    );
}
